import { Link } from "react-router-dom";
import { MessageSquareText, Phone, Search, DollarSign, Tag, HelpCircle } from "lucide-react";
import { siteConfig } from "../config";

/**
 * Reusable CTA strip used between content sections on the public site.
 * Keeps the conversion path consistent across the top-6 pages without
 * relying on popups or exit-intent traps.
 *
 * Props:
 *   variant: "default" | "navy" | "white"
 *   actions: array of { key, label, to?, href?, icon? } — falls back to a
 *            sensible Scott-first quartet (Text / Ask / Find / Trade).
 *   heading: optional heading shown on the left.
 *   sub: optional sub-line under the heading.
 *   testId: data-testid prefix for the strip and its buttons.
 */
const ICON_MAP = {
  text: MessageSquareText,
  call: Phone,
  find: Search,
  payment: DollarSign,
  trade: Tag,
  ask: HelpCircle,
};

const DEFAULTS = [
  { key: "text", label: "Text Scott", href: siteConfig.scott.phoneSms, icon: "text" },
  { key: "ask", label: "Ask Scott", to: "/contact", icon: "ask" },
  { key: "find", label: "Find Me a Vehicle", to: "/find-me-a-vehicle", icon: "find" },
  { key: "trade", label: "Value My Trade", to: "/value-my-trade", icon: "trade" },
];

export default function CtaCallout({
  variant = "default",
  actions,
  heading = "Need a quick answer?",
  sub = "Scott can help — text, ask, or send the basics so he can follow up.",
  testId = "cta-callout",
}) {
  const list = (actions && actions.length ? actions : DEFAULTS).slice(0, 4);
  const wrap =
    variant === "navy"
      ? "bg-[var(--ssc-navy)] text-white"
      : variant === "white"
      ? "bg-white border border-[var(--ssc-line)]"
      : "bg-[var(--ssc-paper)] border border-[var(--ssc-line)]";

  const headColor = variant === "navy" ? "text-white" : "text-[var(--ssc-navy)]";
  const subColor = variant === "navy" ? "text-[var(--ssc-silver)]" : "text-[var(--ssc-ink-2)]";

  return (
    <section
      className={`rounded-2xl ${wrap} px-5 py-5 md:px-7 md:py-6 my-8`}
      data-testid={testId}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <div className={`font-black text-lg md:text-xl ${headColor}`}>{heading}</div>
          {sub && <div className={`text-sm mt-1 ${subColor}`}>{sub}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {list.map((a) => {
            const Icon = ICON_MAP[a.icon] || HelpCircle;
            const cls =
              variant === "navy"
                ? "ssc-btn ssc-btn-primary"
                : a.key === "trade"
                ? "ssc-btn ssc-btn-outline"
                : "ssc-btn ssc-btn-primary";
            const content = (
              <>
                <Icon size={16} /> {a.label}
              </>
            );
            const tid = `${testId}-${a.key}`;
            if (a.href) {
              return (
                <a key={a.key} href={a.href} className={cls} data-testid={tid}>
                  {content}
                </a>
              );
            }
            return (
              <Link key={a.key} to={a.to} className={cls} data-testid={tid}>
                {content}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
